import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { api } from "@/lib/api";
import { Loader2, Settings as SettingsIcon, Plus, LogOut, Globe } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import YusiPermissionsPanel from "@/components/yusi/YusiPermissionsPanel";

export default function Settings() {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useLang();
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/businesses").then((r) => setBusinesses(r.data)).finally(() => setLoading(false));
  }, []);

  const planLabel = user?.plan === "admin"
    ? t("nav.plan.admin")
    : (user?.plan === "free" || !user?.plan)
      ? t("nav.plan.free")
      : t("nav.plan.named", { name: user.plan });

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-cyan-400 mb-2">
          <SettingsIcon size={12} /> {t("settings.eyebrow")}
        </div>
        <h1 className="font-display text-4xl font-bold text-white">{t("settings.title")}</h1>
      </div>

      <div className="glass-strong rounded-3xl p-7">
        <h2 className="font-display text-xl font-semibold text-white mb-4">{t("settings.accountInfo")}</h2>
        <div className="space-y-3 text-sm">
          <Row label={t("settings.email")}>{user?.email}</Row>
          <Row label={t("settings.fullName")}>{user?.full_name || <span className="text-slate-500">{t("common.notSet")}</span>}</Row>
          <Row label={t("settings.plan")}>{planLabel}</Row>
        </div>
      </div>

      {/* Language preference panel */}
      <div className="glass-strong rounded-3xl p-7">
        <div className="flex items-center gap-2 mb-4">
          <Globe size={16} className="text-cyan-300" />
          <h2 className="font-display text-xl font-semibold text-white">{t("settings.langPanel")}</h2>
        </div>
        <p className="text-xs text-slate-400 mb-4 max-w-xl">{t("settings.langPanelHint")}</p>
        <div
          className="inline-flex items-center gap-1 rounded-lg border border-white/10 px-1 py-0.5"
          data-testid="settings-lang-switch"
        >
          <button
            type="button"
            onClick={() => setLang("en")}
            aria-pressed={lang === "en"}
            className={`text-xs font-semibold px-3 py-1.5 rounded-md transition-colors ${
              lang === "en" ? "bg-white/10 text-white" : "text-slate-400 hover:text-white"
            }`}
            data-testid="settings-lang-en"
          >
            English
          </button>
          <span className="text-slate-600 text-xs">|</span>
          <button
            type="button"
            onClick={() => setLang("tr")}
            aria-pressed={lang === "tr"}
            className={`text-xs font-semibold px-3 py-1.5 rounded-md transition-colors ${
              lang === "tr" ? "bg-white/10 text-white" : "text-slate-400 hover:text-white"
            }`}
            data-testid="settings-lang-tr"
          >
            Türkçe
          </button>
        </div>
      </div>

      <YusiPermissionsPanel />

      <div className="glass-strong rounded-3xl p-7">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-semibold text-white">{t("settings.myBusinesses")}</h2>
          <Link to="/app/onboarding" className="ghost-btn rounded-lg px-3 py-1.5 text-xs text-white inline-flex items-center gap-1.5" data-testid="settings-add-business">
            <Plus size={12} /> {t("settings.newBusiness")}
          </Link>
        </div>
        {loading ? (
          <div className="text-slate-400 flex items-center"><Loader2 className="animate-spin mr-2" /> {t("common.loading")}</div>
        ) : businesses.length === 0 ? (
          <div className="text-slate-500 text-sm py-4">{t("settings.noBusinesses")}</div>
        ) : (
          <div className="space-y-2">
            {businesses.map((b) => (
              <Link
                key={b.id}
                to={`/app/isletme/${b.id}/bilgiler`}
                className="block rounded-xl bg-white/[0.02] border border-white/5 hover:border-blue-500/30 hover:bg-blue-500/5 p-4 transition-all"
                data-testid={`settings-biz-${b.id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white font-medium">{b.name}</div>
                    <div className="text-xs text-slate-500">{b.sector} · {b.city}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-slate-500">{t("settings.aiScore")}</div>
                    <div className="font-mono text-lg text-white">{b.latest_score ?? "-"}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      <div className="glass rounded-3xl p-7">
        <h2 className="font-display text-xl font-semibold text-white mb-4">{t("settings.session")}</h2>
        <button
          onClick={() => { logout(); navigate(lang === "en" ? "/" : "/tr"); }}
          className="rounded-lg px-5 py-2.5 text-sm text-red-300 border border-red-500/30 hover:bg-red-500/10 inline-flex items-center gap-2"
          data-testid="settings-logout"
        >
          <LogOut size={14} /> {t("nav.logout")}
        </button>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-6 py-1">
      <div className="text-xs uppercase tracking-wider text-slate-500 sm:w-32">{label}</div>
      <div className="text-white">{children}</div>
    </div>
  );
}
